import React, { useState } from 'react';
import classNames from 'classnames';

import useCollectionGrid from '@lootboy/lib/slices/cardCollections/hooks/useCollectionGrid';
import { ECollectionBannerState } from '@lootboy/lib/slices/cardCollections/types';
import { isEmptyObject } from '@lootboy/lib/utils/records';

import CardSelectionDialog from '~/views/CollectionView/CardSelectionDialog';
import CollectionGridSlot from '~/views/CollectionView/CollectionCardGrid/CollectionGridSlots';
import CollectionGridSpecialGroup, {
  TExtendedSlot,
} from '~/views/CollectionView/CollectionCardGrid/CollectionGridSpecialGroup';
import {
  getGridPlacement,
  getSpecialGroupBoundaries,
} from '~/views/CollectionView/CollectionCardGrid/utils';

import styles from './CollectionCardGrid.module.scss';

interface ICollectionCardGridProps {
  collectionId: string;
}

const CollectionCardGrid: React.FC<ICollectionCardGridProps> = ({
  collectionId,
}) => {
  const gridState = useCollectionGrid(collectionId);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(
    null,
  );

  if (!gridState) return null;
  const {
    gridColumns = 1,
    slots = [],
    specialGroups = [],
    progress = [],
    state = ECollectionBannerState.UPCOMING,
  } = gridState;
  const gridRows = Math.ceil(slots.length / gridColumns);

  const extendedSlots = slots.map((slot, i) => ({
    ...slot,
    insertedCard: progress[i],
  }));

  const populatedSpecialGroups = specialGroups.filter((specialGroup) => {
    return extendedSlots.some((slot) => slot?.specialGroup === specialGroup.id);
  });

  const specialGroupSlots: {
    [key: string]: Array<TExtendedSlot>;
  } = populatedSpecialGroups.reduce(
    (acc, specialGroup) => ({
      ...acc,
      [specialGroup.id]: extendedSlots.filter(
        (extendedSlot) => extendedSlot.specialGroup === specialGroup.id,
      ),
    }),
    {},
  );

  return (
    <>
      {typeof selectedSlotIndex === 'number' && (
        <CardSelectionDialog
          onClose={() => setSelectedSlotIndex(null)}
          collectionId={collectionId}
          slotIndex={selectedSlotIndex}
        />
      )}
      <div
        className={classNames(styles.gridContainer, {
          [styles.disabled]: state !== ECollectionBannerState.AVAILABLE,
        })}
        style={{
          gridTemplateRows: `repeat(${gridRows}, min-content)`,
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
        }}
      >
        {slots.map((slot, i) => {
          if (isEmptyObject(slot)) {
            return null;
          }
          return (
            <CollectionGridSlot
              key={i}
              slot={slot}
              placement={getGridPlacement(i, gridColumns)}
              onClick={() => setSelectedSlotIndex(i)}
              insertedCard={progress[i]}
              collectionState={state ?? ECollectionBannerState.UPCOMING}
            />
          );
        })}
        {getSpecialGroupBoundaries(
          populatedSpecialGroups,
          slots,
          gridColumns,
        ).map(({ start, end, specialGroup }) => (
          <CollectionGridSpecialGroup
            key={specialGroup.id}
            slots={specialGroupSlots[specialGroup.id]}
            specialGroup={specialGroup}
            firstCardPlacement={start}
            lastCardPlacement={end}
          />
        ))}
      </div>
    </>
  );
};

export default React.memo(CollectionCardGrid);
